/**
 * URL/cache-tag sanitization helpers.
 *
 * Lives in its own module so the edge proxy (`proxy.ts`) can import the
 * sanitizer without pulling in `lib/cms.ts` (which dynamically imports
 * `@sentry/nextjs` and the full Payload type surface). `lib/cms.ts`
 * re-exports `sanitizeSlug` from here so existing callers don't break.
 *
 * Root cause this fixes (Sentry MARKETING-SITE-14):
 *
 * Next.js automatically derives an implicit cache tag of the form
 * `_N_T_<pathname>` from the URL-decoded request pathname and adds it to
 * the `x-next-cache-tags` response header. Node's `http.OutgoingMessage`
 * rejects any header value containing a byte outside `\x09` or `\x20-\x7E`
 * with `TypeError: Invalid character in header content`.
 *
 * A real, published blog post at
 * `/ch/en/insights/ahead-health-raises-6m-to-build-europe’s-ai-health-os`
 * (curly apostrophe U+2019, URL-encoded as `%E2%80%99`) hit the page
 * handler with `pathname` containing the decoded `’`, which produced
 * an implicit tag containing the same bytes \xe2\x80\x99 — and
 * `appendHeader` blew up before the response could be sent.
 *
 * Fix: the proxy normalises every request pathname to ASCII-safe form
 * before it reaches the page handler. If the sanitized form differs from
 * the original, the proxy 308-redirects to the canonical (sanitized) URL
 * — which is what `generateStaticParams` ships, so the redirect
 * lands on a real prerendered page.
 *
 * The same `sanitizeSlug` rules used to normalize CMS slugs at build time
 * (`getBlogPosts` etc.) are applied here per path segment, so the
 * canonical URL is always the build-time slug.
 */

/**
 * Sanitize a CMS slug to ASCII-safe characters (Next.js cache tags reject
 * non-ASCII).
 *
 * Accepts `null`/`undefined` defensively: a CMS draft saved with
 * `slug=null` previously crashed `null.normalize()` and killed the entire
 * production build (AHD-6013, blog-posts id=30 on 2026-05-04). Returning
 * `""` here keeps the caller alive; upstream `filterValidDocs` is the
 * primary guard that drops the bad doc before sanitization.
 */
export function sanitizeSlug(slug: string | null | undefined): string {
  if (typeof slug !== "string" || slug.length === 0) return ""
  return slug
    .normalize("NFKD") // decompose ligatures + accents
    .replace(/[‘’‚]/g, "'") // curly single quotes -> straight
    .replace(/[“”„]/g, '"') // curly double quotes -> straight
    .replace(/[–—]/g, "-") // en/em dashes -> hyphen
    .replace(/[^\x20-\x7E]/g, "") // strip remaining non-ASCII
    .replace(/['"]/g, "") // remove quotes from slugs
    .replace(/\s+/g, "-") // spaces -> hyphens
    .replace(/-{2,}/g, "-") // collapse multiple hyphens
    .replace(/^-|-$/g, "") // trim leading/trailing hyphens
}

/**
 * True iff `s` is safe to embed in an HTTP header value. Mirrors the
 * Node.js `http` module's validator: only `\t` and visible ASCII
 * (0x20-0x7E) are allowed. Used by the proxy to decide whether a
 * pathname needs to be redirected to its canonical sanitized form.
 */
export function isHttpHeaderSafe(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i)
    if (code === 0x09) continue
    if (code < 0x20 || code > 0x7e) return false
  }
  return true
}

/**
 * Normalise a request pathname to a header-safe canonical form.
 *
 * Each non-empty segment is run through `sanitizeSlug`; empty segments
 * (leading/trailing slashes, consecutive slashes) are preserved so the
 * redirect doesn't accidentally also strip a trailing slash — that's
 * a separate concern handled by `forwardLocalePath`.
 *
 * Fast-paths the common case where the pathname is already canonical
 * (pure URL-safe ASCII, no whitespace, no quote characters) so we don't
 * pay the per-segment normalize cost on every request.
 *
 * Also catches percent-encoded non-ASCII bytes (e.g. `%E2%80%99` for a
 * curly apostrophe): `URL.pathname` returns the percent-encoded form, so
 * the raw byte scan below would consider it ASCII-only and short-circuit,
 * but Next.js percent-decodes the pathname before building the implicit
 * `_N_T_<pathname>` cache tag — at which point those bytes become
 * non-ASCII again and `http.appendHeader` blows up.
 */
export function sanitizePathnameForHeader(pathname: string): string {
  // Fast path: ASCII-only AND none of the chars `sanitizeSlug` rewrites
  // AND no `%XX` escape whose decoded byte is non-ASCII.
  // Note that `'` (0x27) is here because Next.js percent-decodes a `%27`
  // in the request URL back to a literal `'` before building the
  // implicit cache tag, and `sanitizeSlug` strips it — so the
  // canonical form on disk has no apostrophe.
  let canonical = true
  for (let i = 0; i < pathname.length; i++) {
    const code = pathname.charCodeAt(i)
    if (code === 0x09) continue
    if (code < 0x20 || code > 0x7e) {
      canonical = false
      break
    }
    if (
      code === 0x20 || // space
      code === 0x22 || // "
      code === 0x27 || // '
      code === 0x5c // backslash
    ) {
      canonical = false
      break
    }
    // Percent-encoded byte. Treat as non-canonical if it decodes to
    // anything outside printable ASCII — i.e. first hex nibble is 8-F
    // (byte > 0x7F), or the whole escape decodes to a control char /
    // space / quote / backslash that the rules above already reject in
    // their literal form.
    if (code === 0x25 /* % */) {
      // `charCodeAt` past the end returns `NaN`, so an incomplete escape at
      // the tail (a lone `%` or `%F`) yields `hexDigit(-1)` and is caught too.
      const hi = pathname.charCodeAt(i + 1)
      const lo = pathname.charCodeAt(i + 2)
      const hiDigit = hexDigit(hi)
      const loDigit = hexDigit(lo)
      if (hiDigit < 0 || loDigit < 0) {
        // Malformed `%XX` — the router's `decodeURIComponent` would throw
        // `failed to decode param`. Force canonical=false so the slow path
        // strips the stray `%` (via `sanitizeSlug`) and we redirect to the
        // decode-safe canonical URL.
        canonical = false
        break
      }
      const byte = (hiDigit << 4) | loDigit
      if (
        byte > 0x7e ||
        byte < 0x20 ||
        byte === 0x20 ||
        byte === 0x22 ||
        byte === 0x27 ||
        byte === 0x5c
      ) {
        canonical = false
        break
      }
    }
  }
  if (canonical) return pathname

  // Percent-decode each segment so a `%E2%80%99` (curly apostrophe in
  // UTF-8) is collapsed before per-segment sanitization runs. Decoding
  // per-segment (rather than the whole pathname at once) mirrors how the
  // Next.js router decodes route params and keeps one bad segment from
  // discarding the decode of its neighbours.
  //
  // `decodeURIComponent` throws both on malformed escapes (a lone `%FG`)
  // and on valid-hex escapes that form an invalid UTF-8 sequence (`%c0`,
  // path-traversal probes like `%c0%ae`) — exactly the inputs that make
  // the router throw `failed to decode param`. For those segments we strip
  // every `%` so the canonical form is decode-safe: the proxy redirects to
  // it and the router no longer crashes.
  const segments = pathname.split("/")
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].length === 0) continue
    let segment = segments[i]
    try {
      segment = decodeURIComponent(segment)
    } catch {
      segment = segment.replace(/%/g, "")
    }
    segments[i] = sanitizeSlug(segment)
  }
  return segments.join("/")
}

function hexDigit(code: number): number {
  if (code >= 0x30 && code <= 0x39) return code - 0x30 // 0-9
  if (code >= 0x41 && code <= 0x46) return code - 0x41 + 10 // A-F
  if (code >= 0x61 && code <= 0x66) return code - 0x61 + 10 // a-f
  return -1
}
